/*
** $Id$
**
** Copyright 2018 Port25 Solutions, Inc.
** All rights reserved.
*/


import React, { Component } from 'react';
import { Container, Row, Col, Badge } from 'react-bootstrap';
import Table from './Table';
import PropTypes from 'prop-types';
import { updateComponent } from '../util/Utils'
import { SHORT_REFRESH_INTERVAL } from '../constants'


const UNKNOWN = "unknown"
const SPACE = " "


function makeTrafficData(res, name, what, inOrOut) {
    if (!res || !res.data.data.status.traffic) {
        return [name, 0, 0, 0]
    }
    return [ name, 
        res.data.data.status.traffic[what][inOrOut].rcp,
        res.data.data.status.traffic[what][inOrOut].msg,
        res.data.data.status.traffic[what][inOrOut].kb
    ]
}


function makeConnectionData(res, name, what) {
    if (!res || !res.data.data.status.conn) {
        return [name, 0, 0]
    }
    return [ name, 
        res.data.data.status.conn.smtpIn[what],
        res.data.data.status.conn.smtpOut[what]
    ]
}


function makeDomainNamesData(res, name, what) {
    if (!res || !res.data.data.status.resolver) {
        return [name, 0]
    }
    return [ name, res.data.data.status.resolver[what] ]
}


function makeSpoolData(res, name, what) {
    let total
    if (name === 'files') {
        total = (res && res.data.data.status.spool)
            ? res.data.data.status.spool.files[what] : 0
    }
    else {
        total = (res && res.data.data.status.spool)
            ? res.data.data.status.spool[what] : 0
        if (what === 'initPct') {
            total += "%"
        }
    }

    return [ name, total ]
}


function makeQueueData(res, name, what) {
    if (!res || !res.data.data.status.queue) {
        return [name, 0, 0, 0]
    }
    return [ name, 
        res.data.data.status.queue[what].rcp,
        res.data.data.status.queue[what].dom,
        res.data.data.status.queue[what].kb 
    ]
}


function makeStatusData(res) {
    return {
        status: res ? res.data.data.status.status : UNKNOWN,
        startupTime: res && res.data.data.status.startupTime
            ? res.data.data.status.startupTime : UNKNOWN,
        timeNow: res && res.data.data.status.timeNow
            ? res.data.data.status.timeNow : UNKNOWN
    }
}


function makeTableData(res) {
    return {
        inbound: {
            columns: ["Inbound Traffic", "rcpts", "msgs", "kbytes"],
            data: [
                makeTrafficData(res, 'total', 'total', 'in'),
                makeTrafficData(res, 'last hour', 'lastHr', 'in'),
                makeTrafficData(res, 'top/hour', 'topPerHr', 'in'),
                makeTrafficData(res, 'last minute', 'lastMin', 'in'),
                makeTrafficData(res, 'top/minute', 'topPerMin', 'in'),
            ]
        },
        outbound: {
            columns: ["Outbound Traffic", "rcpts", "msgs", "kbytes"],
            data: [
                makeTrafficData(res, 'total', 'total', 'out'),
                makeTrafficData(res, 'last hour', 'lastHr', 'out'),
                makeTrafficData(res, 'top/hour', 'topPerHr', 'out'),
                makeTrafficData(res, 'last minute', 'lastMin', 'out'),
                makeTrafficData(res, 'top/minute', 'topPerMin', 'out'),
            ]
        },
        connections: {
            columns: ["Connections", "in", "out"],
            data: [
                makeConnectionData(res, 'active', 'cur'),
                makeConnectionData(res, 'top', 'top'),
                makeConnectionData(res, 'max', 'max')
            ]
        },
        domainNames: {
            columns: ["Domain Names", "total"],
            data: [
               makeDomainNamesData(res, 'cached', 'namesCached'),
               makeDomainNamesData(res, 'pending', 'queriesPending')
            ]
        },
        spool: {
            columns: ["Spool", "total"],
            data: [
                makeSpoolData(res, 'files', 'inUse'),
                makeSpoolData(res, 'directories', 'dirs'),
                makeSpoolData(res, 'initialization', 'initPct')
            ]
        },
        queues: {
            columns: ["Queues", "rcpts", "domains", "kbytes"],
            data: [
                makeQueueData(res, 'SMTP', 'smtp'),
                makeQueueData(res, 'discard', 'discard'),
                makeQueueData(res, 'file', 'file'),
                makeQueueData(res, 'pipe', 'pipe')
            ]
        },
        status: makeStatusData(res)
    }
}


/**
 * Pads a "0" before given value if the value is a single digit number.  If the
 * given value is not a single digit number, it is returned unaltered.
 *
 * @param n
 *      what to pad.
 * @return
 *      the padded value.
 */
function pad(n) {
    return n < 10 ? "0" + n : n
}


/**
 * Status page of web monitor.
 */
export default class Status extends Component {
    constructor(props) {
        super(props)
        this.refresh = this.refresh.bind(this)
        this.refreshInterval = this.refreshInterval.bind(this)
        this.dateDiff = this.dateDiff.bind(this)
        this.addDateTimeSep = this.addDateTimeSep.bind(this)
    }


    static propTypes = {
        showError: PropTypes.func.isRequired,
        setRefresh: PropTypes.func.isRequired,
        setRefreshInterval: PropTypes.func.isRequired
    };


    componentDidMount() {
        this.props.setRefresh(this.refresh)
        this.props.setRefreshInterval(this.refreshInterval)
        this.refresh()        
    }


    refresh() {
        updateComponent(this, '/status')
    }


    refreshInterval() {
        return SHORT_REFRESH_INTERVAL
    }


    dateDiff(date1, date2) {
        if (date1 === UNKNOWN || date2 === UNKNOWN) {
            return UNKNOWN
        }
        let d1 = this.addDateTimeSep(date1)
        let d2 = this.addDateTimeSep(date2)
        let ms = new Date(d2) - new Date(d1)
        let dd, hh, mm, ss
        ss = Math.floor(ms / 1000)
        mm = Math.floor(ss / 60)
        ss = ss % 60
        hh = Math.floor(mm / 60)
        mm = mm % 60
        dd = Math.floor(hh / 24)
        hh = hh % 24
        return dd + " " + pad(hh) + ":" + pad(mm) + ":" + pad(ss)
    }


    addDateTimeSep(date) {
        // Safari fails to parse the date without date/time separator, although
        // strictly speaking it is optional for an ISO formatted date:
        // @see https://stackoverflow.com/questions/4310953/invalid-date-in-safari
        return date.replace(SPACE, 'T')
    }


    render() {
        let res = makeTableData(this.state)
        return (
            <Container>
                <Row>
                    <Col md={6}>
                        <Table columns={res.inbound.columns}
                            data={res.inbound.data}/>
                    </Col>
                    <Col md={6}>
                        <Table columns={res.outbound.columns}
                            data={res.outbound.data}/>
                    </Col>
                </Row>
                <Row>
                    <Col md={6}>
                        <Table columns={res.connections.columns}
                            data={res.connections.data}/>
                    </Col>
                    <Col md={3}>
                        <Table columns={res.domainNames.columns}
                            data={res.domainNames.data}/>
                    </Col>
                    <Col md={3}>
                        <Table columns={res.spool.columns}
                            data={res.spool.data}/>
                    </Col>
                </Row>
                <Row>
                    <Col md={12}>
                        <Table columns={res.queues.columns}
                            data={res.queues.data}/>
                    </Col>
                </Row>
                <Row>
                    <Col md={12}>
                        <div className="center">
                            Status
                            <Badge variant='secondary'>
                                {res.status.status}
                            </Badge>
                            Started On
                            <Badge variant='secondary'>
                                {res.status.startupTime}
                            </Badge>
                            Uptime
                            <Badge variant='secondary'>
                                {this.dateDiff(res.status.startupTime, res.status.timeNow)}
                            </Badge>
                        </div>
                    </Col>
                </Row>
            </Container>
        );
    }
}
